import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { FileUploadModule } from 'primeng/fileupload';
import { GalleriaModule } from 'primeng/galleria';
import { MessageService } from 'src/app/services/message.service';
import { ImageOwnerType, ImageService, MAX_IMAGE_BYTES, MAX_IMAGES_PER_PRODUCT } from 'src/app/services/image.service';
import { ProductImageFieldsFragment } from 'src/gql/generated';

@Component({
  selector: 'app-product-image-gallery',
  templateUrl: './product-image-gallery.component.html',
  styleUrls: ['./product-image-gallery.component.sass'],
  standalone: true,
  imports: [ButtonModule, ConfirmDialogModule, FileUploadModule, GalleriaModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [ConfirmationService],
})
export class ProductImageGalleryComponent implements OnInit {
  @Input() ownerType!: ImageOwnerType;
  @Input() ownerId!: number;
  @Input() images: ProductImageFieldsFragment[] = [];
  @Input() canEdit = false;
  @Output() imagesChanged = new EventEmitter<void>();

  readonly maxImages = MAX_IMAGES_PER_PRODUCT;
  readonly maxBytes = MAX_IMAGE_BYTES;
  readonly acceptMime = 'image/jpeg,image/png,image/webp';

  uploading = false;
  galleriaVisible = false;
  activeIndex = 0;
  dragOver = false;
  private dragDepth = 0;

  constructor(
    private imageService: ImageService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit(): void {
    if (!this.ownerType || !this.ownerId) {
      throw new Error('product-image-gallery: ownerType and ownerId are required');
    }
  }

  get sortedImages(): ProductImageFieldsFragment[] {
    return [...(this.images ?? [])].sort((a, b) => a.position - b.position);
  }

  get reachedLimit(): boolean {
    return (this.images?.length ?? 0) >= this.maxImages;
  }

  onUploadSelected(event: { files: File[] }, fileUpload?: { clear: () => void }): void {
    const file = event.files[0];
    if (!file) {
      return;
    }
    this.uploadFile(file, fileUpload);
  }

  private uploadFile(file: File, fileUpload?: { clear: () => void }): void {
    this.uploading = true;
    this.imageService.upload(this.ownerType, this.ownerId, file).subscribe({
      next: () => {
        this.uploading = false;
        fileUpload?.clear();
        this.messageService.setFlashMessageWithClear('success', '完了', '画像をアップロードしました。');
        this.imagesChanged.emit();
      },
      error: (e: Error) => {
        this.uploading = false;
        fileUpload?.clear();
        this.messageService.setFlashMessage('error', 'エラー', e?.message ?? 'アップロードに失敗しました。');
      },
    });
  }

  onDragEnter(event: DragEvent): void {
    if (!this.canEdit) {
      return;
    }
    event.preventDefault();
    this.dragDepth++;
    this.dragOver = true;
  }

  onDragOver(event: DragEvent): void {
    if (!this.canEdit) {
      return;
    }
    event.preventDefault();
  }

  onDragLeave(event: DragEvent): void {
    if (!this.canEdit) {
      return;
    }
    event.preventDefault();
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) {
      this.dragOver = false;
    }
  }

  onDrop(event: DragEvent): void {
    if (!this.canEdit) {
      return;
    }
    event.preventDefault();
    this.dragDepth = 0;
    this.dragOver = false;
    if (this.uploading || this.reachedLimit) {
      return;
    }
    const file = Array.from(event.dataTransfer?.files ?? [])[0];
    if (!file) {
      return;
    }
    if (!this.acceptMime.split(',').includes(file.type)) {
      this.messageService.setFlashMessage('error', 'エラー', '対応形式はJPG / PNG / WebPです。');
      return;
    }
    if (file.size > this.maxBytes) {
      this.messageService.setFlashMessage('error', 'エラー', 'ファイルサイズは20MB以下にしてください。');
      return;
    }
    this.uploadFile(file);
  }

  onDelete(image: ProductImageFieldsFragment): void {
    this.confirmationService.confirm({
      message: 'この画像を削除しますか?',
      header: '画像削除確認',
      acceptLabel: '削除',
      rejectLabel: 'キャンセル',
      accept: () => {
        this.imageService.delete(image.id).subscribe({
          next: (ok) => {
            if (ok) {
              this.messageService.setFlashMessageWithClear('success', '完了', '画像を削除しました。');
              this.imagesChanged.emit();
            } else {
              this.messageService.setFlashMessage('error', 'エラー', '削除に失敗しました。');
            }
          },
          error: (e: Error) => {
            this.messageService.setFlashMessage('error', 'エラー', e?.message ?? '削除に失敗しました。');
          },
        });
      },
    });
  }

  openGalleria(index: number): void {
    this.activeIndex = index;
    this.galleriaVisible = true;
  }

  trackById(_index: number, image: ProductImageFieldsFragment): string {
    return image.id;
  }

  onImageError(event: Event, image: ProductImageFieldsFragment): void {
    // eslint-disable-next-line no-console
    console.error('画像の読み込みに失敗しました', { id: image.id, url: image.url, event });
  }
}
